import type { AssayPublicGetMany, AssayRunConfig, AssayRunConfigControlSample } from '@/types';

/** Matched by `assay_id`, not `assay_type`: several assay versions can share one type (e.g. Tau
 *  V1 and V2 are both 'tau_proteoform'), so a type match would silently pick the first. */
export function findAssayRunConfig(
  assayRunConfigs: AssayRunConfig[],
  selectedAssay: Pick<AssayPublicGetMany, 'id'> | undefined,
): AssayRunConfig | undefined {
  if (!selectedAssay) return undefined;

  return assayRunConfigs.find(rc => rc.assay_id === selectedAssay.id);
}

/** For callers that hold only an assay id, so they need not fake a partial assay object. */
export function findRunConfigByAssayId(
  assayRunConfigs: AssayRunConfig[],
  assayId: string | undefined,
): AssayRunConfig | undefined {
  return assayId ? findAssayRunConfig(assayRunConfigs, { id: assayId }) : undefined;
}

// Last-resort options for a misconfigured assay, or before /assays/run_configs resolves. Never a
// substitute for the run config: Tau V1 only accepts 12 and 24, so offering 4/36 invites a 422.
export const SAMPLE_COUNT_FALLBACK: readonly number[] = [4, 12, 24, 36];

/** Sample-count options for an assay, from its run config. `[]` from the backend counts as absent. */
export function validSampleCounts(runConfig: AssayRunConfig | undefined): readonly number[] {
  return runConfig?.valid_sample_counts && runConfig.valid_sample_counts.length > 0
    ? runConfig.valid_sample_counts
    : SAMPLE_COUNT_FALLBACK;
}

/** As {@link validSampleCounts}, but stringified for the `noOfSamples` form field and Selects. */
export function sampleCountOptionsFor(runConfig: AssayRunConfig | undefined): string[] {
  return validSampleCounts(runConfig).map(String);
}

/** Last resort before /assays/run_configs resolves — a single, unnamed group. */
export const LIBRARY_GROUP_COUNT_FALLBACK: readonly number[] = [1];

/** Controls[] mixes library-prep and detection-kit entries; only the former carry a real
 *  library_group. Filtering here keeps every count/ordering derivation consistent. */
export function libraryPrepControls(runConfig: AssayRunConfig | undefined): AssayRunConfigControlSample[] {
  return runConfig?.controls.filter(c => c.control_type === 'library_prep_control') ?? [];
}

/** Batch-count options for an assay, from its run config. `[]` from the backend counts as absent. */
export function validProteinLibraryCounts(runConfig: AssayRunConfig | undefined): readonly number[] {
  return runConfig?.valid_protein_library_counts && runConfig.valid_protein_library_counts.length > 0
    ? runConfig.valid_protein_library_counts
    : LIBRARY_GROUP_COUNT_FALLBACK;
}

/** Mandatory-first, then optional in declared order (matches the backend's `_build_run_config`).
 *  Returns the same control object references as `runConfig.controls` (not cloned) so callers can
 *  key "is this active" by reference — two library-prep controls can share a well_position. */
export function orderedLibraryPrepControls(runConfig: AssayRunConfig | undefined): AssayRunConfigControlSample[] {
  return [...libraryPrepControls(runConfig)].toSorted((a, b) => Number(a.is_optional) - Number(b.is_optional));
}

/** True below the assay's max valid sample count (e.g. 12 of [12, 24]): only one library group
 *  fits, so the count is pinned to the assay's smallest option. Shared by activeLibraryGroupNames,
 *  control-row derivation and the count dropdown's disabled state so they can never disagree. */
export function isLibraryGroupCountLocked(runConfig: AssayRunConfig | undefined, numSamples: number): boolean {
  if (!runConfig || runConfig.valid_sample_counts.length === 0) return false;
  return numSamples < Math.max(...runConfig.valid_sample_counts);
}

/** The count actually in effect: the assay's smallest valid count while locked (capping a count
 *  carried over from a larger sample size), otherwise the caller's selection. */
export function effectiveLibraryGroupCount(
  runConfig: AssayRunConfig | undefined,
  numSamples: number,
  libraryGroupCount: number,
): number {
  if (!isLibraryGroupCountLocked(runConfig, numSamples)) return libraryGroupCount;
  return Math.min(...validProteinLibraryCounts(runConfig));
}

/** Default group for a customer row: contiguous index blocks across the active groups, so every
 *  row gets a value without manual entry (still editable in the grid). */
export function defaultLibraryGroupForIndex(
  index: number,
  rowCount: number,
  activeGroups: readonly string[],
): string | null {
  if (activeGroups.length === 0) return null;
  if (activeGroups.length === 1) return activeGroups[0] ?? null;
  const perGroup = Math.ceil(rowCount / activeGroups.length);
  const groupIndex = Math.min(Math.floor(index / perGroup), activeGroups.length - 1);
  return activeGroups[groupIndex] ?? null;
}

/** Shared by control-row derivation and customer-row validation/dropdown wiring so they can never
 *  disagree — including the single-group lock below the assay's max sample count. */
export function activeLibraryGroupNames(
  runConfig: AssayRunConfig | undefined,
  numSamples: number,
  libraryGroupCount: number,
): string[] {
  if (!runConfig) return [];
  const count = effectiveLibraryGroupCount(runConfig, numSamples, libraryGroupCount);
  const active = orderedLibraryPrepControls(runConfig).slice(0, count);
  return active.map(c => c.library_group).filter((g): g is string => Boolean(g));
}
